import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { Star, X, Check } from "lucide-react";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const Body = styled.div`
  padding: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
  }
`;

const UserOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: ${(p) => (p.$selected ? "#eff6ff" : "#f9fafb")};
  border: 2px solid ${(p) => (p.$selected ? "#3b82f6" : "transparent")};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${(p) => (p.$selected ? "#eff6ff" : "#f3f4f6")};
  }
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 500;
  color: #1f2937;
`;

const UserTag = styled.span`
  font-size: 0.75rem;
  color: ${(p) => (p.$accepted ? "#059669" : "#6b7280")};
  background: ${(p) => (p.$accepted ? "#d1fae5" : "#f3f4f6")};
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 0.5rem;
`;

const CheckCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(p) => (p.$selected ? "#3b82f6" : "#e5e7eb")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  svg {
    color: white;
    opacity: ${(p) => (p.$selected ? 1 : 0)};
  }
`;

const RatingSection = styled.div`
  margin-bottom: 1.5rem;
`;

const StarRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const StarBtn = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  transition: transform 0.1s;

  &:hover {
    transform: scale(1.1);
  }

  svg {
    color: ${(p) => (p.$filled ? "#f59e0b" : "#d1d5db")};
    fill: ${(p) => (p.$filled ? "#f59e0b" : "none")};
    transition: all 0.15s;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.15s;
  margin-bottom: 1.5rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SkipBtn = styled(Button)`
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`;

const SubmitBtn = styled(Button)`
  background: #3b82f6;
  color: white;

  &:hover:not(:disabled) {
    background: #2563eb;
  }
`;


const ReviewModal = ({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
  offers = [],
  questions = [],
  taskTitle,
  submitting = false,
  existingReview = null, // For editing existing review
}) => {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [hoverRating, setHoverRating] = useState(0);

  // Initialize with existing review data when editing
  React.useEffect(() => {
    if (existingReview && isOpen) {
      setSelectedUserId(existingReview.reviewee_id?.toString() || "");
      setRating(existingReview.rating || 0);
      setFeedback(existingReview.feedback || "");
    }
  }, [existingReview, isOpen]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      if (!existingReview) {
        setSelectedUserId("");
        setRating(0);
        setFeedback("");
      }
      setHoverRating(0);
    }
  }, [isOpen, existingReview]);

  // Build list of reviewable users
  const reviewableUsers = useMemo(() => {
    const userMap = new Map();

    // Helper to get user data from offer (handles nested user object)
    const getOfferUser = (offer) => {
      // API returns user data nested in offer.user
      if (offer.user?.id) {
        return {
          id: offer.user.id,
          first_name: offer.user.first_name,
          last_name: offer.user.last_name,
          avatar: offer.user.avatar,
        };
      }
      // Fallback for flat structure
      return {
        id: offer.user_id,
        first_name: offer.first_name,
        last_name: offer.last_name,
        avatar: offer.avatar,
      };
    };

    // Add accepted offer user first (highest priority)
    const acceptedOffer = offers.find((o) => o.status === "accepted" && (o.user?.id || o.user_id));
    if (acceptedOffer) {
      const user = getOfferUser(acceptedOffer);
      if (user.id) {
        userMap.set(user.id, {
          ...user,
          tag: "Accepted Offer",
          offer_id: acceptedOffer.id,
        });
      }
    }

    // Add other offer users (include all except withdrawn)
    offers.forEach((offer) => {
      const user = getOfferUser(offer);
      if (user.id && !userMap.has(user.id) && offer.status !== "withdrawn") {
        userMap.set(user.id, {
          ...user,
          tag: offer.status === "accepted" ? "Accepted Offer" : "Made Offer",
          offer_id: offer.id,
        });
      }
    });

    // Add question askers
    questions.forEach((q) => {
      if (q.user_id && !userMap.has(q.user_id)) {
        // Questions API returns user_name instead of first_name/last_name
        const nameParts = (q.user_name || "").split(" ");
        const firstName = q.first_name || nameParts[0] || "";
        const lastName = q.last_name || nameParts.slice(1).join(" ") || "";

        userMap.set(q.user_id, {
          id: q.user_id,
          first_name: firstName,
          last_name: lastName,
          avatar: q.avatar,
          tag: "Asked Question",
          offer_id: null,
        });
      }
    });

    return Array.from(userMap.values());
  }, [offers, questions]);

  // Auto-select accepted offer user
  React.useEffect(() => {
    if (reviewableUsers.length > 0 && !selectedUserId) {
      setSelectedUserId(reviewableUsers[0].id.toString());
    }
  }, [reviewableUsers, selectedUserId]);

  const selectedUser = reviewableUsers.find(
    (u) => u.id && u.id.toString() === selectedUserId
  );

  const handleSubmit = () => {
    if (!selectedUserId || rating === 0) return;
    onSubmit({
      reviewee_id: parseInt(selectedUserId),
      rating,
      feedback: feedback.trim() || null,
      offer_id: selectedUser?.offer_id || null,
    });
  };

  const handleSkip = () => {
    onSkip?.();
    onClose();
  };

  if (!isOpen) return null;

  // If no users to review, just close
  if (reviewableUsers.length === 0) {
    return null;
  }

  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{existingReview ? "Edit Review" : "Leave a Review"}</Title>
          <CloseBtn onClick={onClose}>
            <X size={18} />
          </CloseBtn>
        </Header>

        <Body>
          <Label>
            {reviewableUsers.length === 1 ? "Reviewing" : "Who helped you complete this task?"}
          </Label>
          <UserList>
            {reviewableUsers.map((user) => (
              <UserOption
                key={user.id}
                type="button"
                $selected={user.id?.toString() === selectedUserId}
                onClick={() => setSelectedUserId(user.id.toString())}
              >
                <Avatar
                  src={user.avatar || "/default-avatar.png"}
                  alt=""
                />
                <UserInfo>
                  <UserName>
                    {user.first_name} {user.last_name}
                    <UserTag $accepted={user.tag === "Accepted Offer"}>
                      {user.tag}
                    </UserTag>
                  </UserName>
                </UserInfo>
                <CheckCircle $selected={user.id?.toString() === selectedUserId}>
                  <Check size={14} />
                </CheckCircle>
              </UserOption>
            ))}
          </UserList>

          <RatingSection>
            <Label>Rating</Label>
            <StarRow>
              {[1, 2, 3, 4, 5].map((star) => (
                <StarBtn
                  key={star}
                  $filled={star <= (hoverRating || rating)}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  type="button"
                >
                  <Star size={32} />
                </StarBtn>
              ))}
            </StarRow>
          </RatingSection>

          <Label>Feedback (optional)</Label>
          <TextArea
            placeholder="Share your experience working with this person..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            maxLength={1000}
          />
        </Body>

        <Footer>
          {!existingReview && (
            <SkipBtn onClick={handleSkip} disabled={submitting}>
              Skip
            </SkipBtn>
          )}
          <SubmitBtn
            onClick={handleSubmit}
            disabled={!selectedUserId || rating === 0 || submitting}
            style={existingReview ? { flex: "1 1 100%" } : undefined}
          >
            {submitting
              ? "Saving..."
              : existingReview
              ? "Update Review"
              : "Submit Review"}
          </SubmitBtn>
        </Footer>
      </ModalCard>
    </Overlay>
  );
};

export default ReviewModal;
